import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { AlertTriangle, FileWarning, X, FileText, CheckCircle2, FileCheck } from 'lucide-react';

/**
 * PDFDisclaimer - Modal shown when user uploads a PDF file.
 * Explains high-fidelity DOCX conversion and what may need manual review.
 */
export default function PDFDisclaimer({
  isOpen,
  fileName,
  onProceed,
  onCancel,
  uploadContext = "analysis"
}) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      data-testid="pdf-disclaimer-modal"
    >
      <Card className="w-full max-w-lg bg-white relative overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <FileCheck className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">
              {uploadContext === "pptx" ? "PPTX Upload Notice" : "PDF Upload Notice"}
            </h3>
            <p className="text-sm text-blue-100">Please read before proceeding</p>
          </div>
          <button
            onClick={onCancel}
            className="ml-auto p-1 rounded-full hover:bg-white/20 transition-colors"
            data-testid="pdf-disclaimer-close"
          >
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        <div className="p-6">
          {/* File name */}
          <div className="flex items-center gap-3 bg-zinc-50 rounded-lg p-3 mb-4">
            <FileText className="h-5 w-5 text-zinc-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-zinc-500">Selected file</p>
              <p className="text-sm font-medium text-zinc-900 truncate">{fileName}</p>
            </div>
          </div>

          {/* Pre-translation notice — required message */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-900 leading-relaxed mb-2">
              This PDF has been converted into an editable Word document while preserving
              formatting as closely as possible.
            </p>
            <p className="text-sm text-blue-900 leading-relaxed mb-2">
              Most headings, tables, text styling, colors, and document structure will be retained.
            </p>
            <p className="text-sm text-blue-800 leading-relaxed">
              Complex layouts such as multi-column pages, charts, forms, watermarks, floating
              objects, and scanned PDFs may require manual review after translation.
            </p>
          </div>

          {/* What may need review */}
          <div className="mb-5">
            <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-2">
              May require manual review
            </p>
            <ul className="space-y-1.5 text-sm text-zinc-600">
              <li className="flex items-start gap-2">
                <FileWarning className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <span>Multi-column layouts and floating text boxes</span>
              </li>
              <li className="flex items-start gap-2">
                <FileWarning className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <span>Charts, diagrams, watermarks, and background images</span>
              </li>
              <li className="flex items-start gap-2">
                <FileWarning className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <span>Scanned PDFs (image-only, no selectable text)</span>
              </li>
              <li className="flex items-start gap-2">
                <FileWarning className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <span>Interactive form fields and hyperlinks</span>
              </li>
            </ul>
          </div>

          {/* Export note */}
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 mb-5">
            <p className="text-xs text-zinc-600">
              <strong>Output format:</strong> The translated document will be downloaded as a{' '}
              <strong>.docx</strong> file (Word document).
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={onCancel}
              className="flex-1 gap-2"
              data-testid="pdf-disclaimer-cancel"
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <Button
              onClick={onProceed}
              className="flex-1 gap-2 bg-blue-600 hover:bg-blue-700"
              data-testid="pdf-disclaimer-proceed"
            >
              <CheckCircle2 className="h-4 w-4" />
              Proceed
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
